import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth, API } from "@/AuthContext";
import { Mic, Square, Type, Sparkles, Save, Loader2 } from "lucide-react";

export default function NewSession() {
  const { t, lang } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(params.get("patient") || "");
  const [mode, setMode] = useState("text"); // text | voice
  const [notes, setNotes] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [soap, setSoap] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/patients`).then((r) => setPatients(r.data)).catch(() => {});
  }, []);

  // recording timer
  useEffect(() => {
    if (recording) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [recording]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribe(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      alert("Mic access denied: " + e.message);
    }
  };

  const stopRec = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "audio.webm");
      fd.append("language", lang);
      const r = await axios.post(`${API}/transcribe?language=${lang}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNotes((prev) => (prev ? prev + "\n" : "") + r.data.text);
    } catch (e) {
      alert("Transcription failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setTranscribing(false);
    }
  };

  const generateSOAP = async () => {
    if (!notes.trim()) return alert(t.session.noNotes);
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/generate-soap`, { raw_notes: notes, language: lang });
      setSoap(r.data);
    } catch (e) {
      alert("Generation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const saveSession = async () => {
    if (!patientId) return alert(t.session.noPatient);
    if (!notes.trim()) return alert(t.session.noNotes);
    setSaving(true);
    try {
      const r = await axios.post(`${API}/sessions`, {
        patient_id: patientId,
        raw_notes: notes,
        input_type: mode,
        language: lang,
      });
      navigate(`/dashboard/patients/${patientId}?session=${r.data.session_id}`);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-session-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-pine">{t.nav.newSession}</div>
        <h1 className="mt-2 font-heading text-3xl tracking-tight">{t.session.title}</h1>
      </div>

      {/* Patient select */}
      <div className="bg-surface border border-hairline rounded-2xl p-5">
        <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">{t.session.selectPatient}</label>
        <select
          value={patientId}
          onChange={(e) => setPatientId(e.target.value)}
          className="mt-2 w-full px-3 py-2.5 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none"
          data-testid="patient-select"
        >
          <option value="">—</option>
          {patients.map((p) => (
            <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}{p.diagnosis ? ` · ${p.diagnosis}` : ""}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Input pane */}
        <div className="lg:col-span-5 bg-surface border border-hairline rounded-2xl p-6">
          <div className="flex items-center gap-2 p-1 bg-pine-50/60 rounded-full w-fit">
            <button onClick={() => setMode("text")} className={`px-4 py-1.5 rounded-full text-sm transition ${mode === "text" ? "bg-pine text-bone" : "text-ink-muted hover:text-ink"}`} data-testid="mode-text">
              <Type className="inline w-4 h-4 mr-1" /> {t.session.textTab}
            </button>
            <button onClick={() => setMode("voice")} className={`px-4 py-1.5 rounded-full text-sm transition ${mode === "voice" ? "bg-pine text-bone" : "text-ink-muted hover:text-ink"}`} data-testid="mode-voice">
              <Mic className="inline w-4 h-4 mr-1" /> {t.session.voiceTab}
            </button>
          </div>

          {mode === "voice" && (
            <div className="mt-6 flex flex-col items-center justify-center py-8">
              {!recording ? (
                <button
                  onClick={startRec}
                  disabled={transcribing}
                  className="w-24 h-24 rounded-full bg-pine text-bone flex items-center justify-center hover:bg-pine-hover transition shadow-floating disabled:opacity-50"
                  data-testid="record-start-btn"
                >
                  <Mic className="w-9 h-9" strokeWidth={1.75} />
                </button>
              ) : (
                <button
                  onClick={stopRec}
                  className="w-24 h-24 rounded-full bg-terracotta text-white flex items-center justify-center hover:bg-terracotta/90 transition shadow-floating animate-pulse-dot"
                  data-testid="record-stop-btn"
                >
                  <Square className="w-8 h-8" fill="white" />
                </button>
              )}
              <div className="mt-5 text-sm text-ink-muted">
                {recording ? (
                  <span className="inline-flex items-center gap-3">
                    <Waveform />
                    <span className="font-mono text-terracotta">{Math.floor(elapsed / 60).toString().padStart(2, "0")}:{(elapsed % 60).toString().padStart(2, "0")}</span>
                    {t.session.recording}
                  </span>
                ) : transcribing ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t.session.transcribing}</span>
                ) : "Tap mic"}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">Notes</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={t.session.placeholderText} rows={10}
              className="mt-2 w-full px-3 py-3 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none font-body text-sm leading-relaxed"
              data-testid="notes-textarea"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={generateSOAP}
              disabled={generating || !notes.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-pine text-bone hover:bg-pine-hover disabled:opacity-50 transition"
              data-testid="generate-soap-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? t.session.generating : (soap ? t.session.regenerate : t.session.generate)}
            </button>
            <button
              onClick={saveSession}
              disabled={saving || !patientId || !notes.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-hairline hover:bg-pine-50 disabled:opacity-50 transition"
              data-testid="save-session-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t.session.saving : t.session.save}
            </button>
          </div>
        </div>

        {/* SOAP pane */}
        <div className="lg:col-span-7 bg-surface border border-hairline rounded-2xl">
          <div className="px-6 py-4 border-b border-hairline">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-pine">SOAP Report</div>
            <div className="mt-1 text-sm text-ink-muted">Powered by Claude Sonnet 4.5</div>
          </div>
          {soap ? (
            <SOAPEditableGrid soap={soap} setSoap={setSoap} t={t} />
          ) : (
            <div className="p-10 text-center text-ink-muted">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-heading text-lg">
                {generating ? t.session.generating : "Enter notes and click generate"}
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Waveform() {
  return (
    <span className="inline-flex items-end gap-[3px] h-7">
      {[0, 0.1, 0.2, 0.3, 0.15, 0.05].map((d, i) => (
        <span key={i} className="waveform-bar" style={{ animationDelay: `${d}s` }} />
      ))}
    </span>
  );
}

function SOAPEditableGrid({ soap, setSoap, t }) {
  const sections = [
    { key: "subjective", label: t.session.sectionS },
    { key: "objective", label: t.session.sectionO },
    { key: "assessment", label: t.session.sectionA },
    { key: "plan", label: t.session.sectionP },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-hairline">
      {sections.map((s, i) => (
        <div key={s.key} className={`p-5 ${i >= 2 ? "border-t border-hairline" : ""}`}>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-pine">{s.label}</div>
          <textarea
            value={soap[s.key] || ""}
            onChange={(e) => setSoap({ ...soap, [s.key]: e.target.value })}
            rows={6}
            className="mt-3 w-full bg-transparent text-sm leading-relaxed focus:outline-none resize-none"
            data-testid={`soap-${s.key}`}
          />
        </div>
      ))}
    </div>
  );
}
